import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { ThemedCard } from '@/components/ui/themed-card';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { 
  Calendar, 
  DollarSign, 
  Users, 
  Trophy, 
  Eye, 
  Rocket,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Sparkles,
  Target,
  Gift,
  Hash,
  Zap,
  Shield,
  Star,
  ArrowLeft,
  AlertTriangle,
  Info
} from 'lucide-react';

interface Raffle {
  id: string;
  title: string;
  description: string;
  type: 'numbers' | 'names' | 'animals' | 'custom';
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  total_spots: number;
  spot_price: number;
  prize_description: string;
  draw_date: string;
  creator_id: string;
  creator_name?: string;
  creator_email?: string;
  created_at: string;
  updated_at: string;
  custom_items?: string[];
}

const RafflePreviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [raffle, setRaffle] = useState<Raffle | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [selectedTab, setSelectedTab] = useState('preview');
  const [hoveredSpot, setHoveredSpot] = useState<number | null>(null);
  const [simulatedParticipants, setSimulatedParticipants] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      fetchRaffle();
    }
  }, [id]);

  useEffect(() => {
    if (raffle) {
      generateSimulatedParticipants();
    }
  }, [raffle]);

  const fetchRaffle = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('raffles_with_creator')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        // Verificar se o usuário é o criador
        if (!user || user.id !== data.creator_id) {
          toast({
            title: 'Acesso negado',
            description: 'Você não tem permissão para visualizar esta prévia.',
            variant: 'destructive',
          });
          navigate('/');
          return;
        }

        // Se não for rascunho, redirecionar para página normal da rifa
        if (data.status !== 'draft') {
          navigate(`/raffle/${id}`);
          return;
        }

        setRaffle(data);
      }
    } catch (error) {
      console.error('Error fetching raffle:', error);
      toast({
        title: 'Erro ao carregar rifa',
        description: 'Não foi possível carregar os dados da rifa.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setActivating(true);
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('credits_balance')
        .eq('id', user?.id)
        .single();

      if (!profile || profile.credits_balance < 10) {
        toast({
          title: 'Saldo insuficiente',
          description: 'Você precisa de 10 créditos para ativar a rifa.',
          variant: 'destructive',
        });
        navigate('/credits');
        return;
      }

      // Chamar função de ativação
      const { data: result, error } = await supabase.rpc('activate_raffle_with_payment', {
        p_raffle_id: id,
        p_user_id: user?.id
      });

      if (error) throw error;

      if (result?.success) {
        toast({
          title: 'Rifa ativada com sucesso!',
          description: 'Sua rifa está agora ativa e disponível para participações.',
        });
        navigate(`/raffle/${id}`);
      } else {
        throw new Error(result?.error || 'Erro ao ativar rifa');
      }
    } catch (error: any) {
      console.error('Error activating raffle:', error);
      toast({
        title: 'Erro ao ativar rifa',
        description: error.message || 'Não foi possível ativar a rifa.',
        variant: 'destructive',
      });
    } finally {
      setActivating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const generateSimulatedParticipants = () => {
    if (!raffle || !raffle.total_spots) return;
    
    const names = [
      { name: 'João Silva', country: '🇧🇷', numbers: [1, 15, 23, 45, 67] },
      { name: 'Maria Santos', country: '🇧🇷', numbers: [2, 8, 31, 44] },
      { name: 'Carlos Rodriguez', country: '🇪🇸', numbers: [3, 12, 28, 55, 72, 89] },
      { name: 'Anna Müller', country: '🇩🇪', numbers: [4, 19, 33] },
      { name: 'James Wilson', country: '🇺🇸', numbers: [5, 21, 47, 68, 91] },
      { name: 'Sophie Martin', country: '🇫🇷', numbers: [6, 16, 39, 52] },
      { name: 'Luigi Rossi', country: '🇮🇹', numbers: [7, 24, 41, 63, 85] },
      { name: 'Yuki Tanaka', country: '🇯🇵', numbers: [9, 18, 36, 59] },
      { name: 'Pedro Oliveira', country: '🇧🇷', numbers: [10, 26, 48, 71, 94] },
      { name: 'Emma Johnson', country: '🇬🇧', numbers: [11, 29, 51, 74] },
      { name: 'Miguel Torres', country: '🇲🇽', numbers: [13, 32, 54, 77] },
      { name: 'Isabella Costa', country: '🇧🇷', numbers: [14, 35, 58, 81] },
      { name: 'Lucas Ferreira', country: '🇵🇹', numbers: [17, 38, 61, 84] },
      { name: 'Elena Popova', country: '🇷🇺', numbers: [20, 42, 65, 88] },
      { name: 'Ahmed Hassan', country: '🇪🇬', numbers: [22, 46, 69, 92] },
      { name: 'Chen Wei', country: '🇨🇳', numbers: [25, 49, 73, 96] },
      { name: 'Priya Sharma', country: '🇮🇳', numbers: [27, 53, 76, 99] },
      { name: 'Gabriel Lima', country: '🇧🇷', numbers: [30, 56, 79] },
      { name: 'Sofia Andersson', country: '🇸🇪', numbers: [34, 60, 83] },
      { name: 'Daniel Kim', country: '🇰🇷', numbers: [37, 64, 87] }
    ];

    // Ajusta os números para ficarem dentro do range da rifa
    const adjustedParticipants = names.map(participant => ({
      ...participant,
      numbers: participant.numbers
        .filter(n => n <= raffle.total_spots)
        .slice(0, Math.min(5, Math.ceil(raffle.total_spots / 20)))
    })).filter(p => p.numbers.length > 0);

    const participantsToShow = adjustedParticipants.slice(0, Math.min(15, Math.max(3, Math.floor(raffle.total_spots * 0.3))));
    setSimulatedParticipants(participantsToShow);
  };

  const getReservedNumbers = () => {
    const reserved = new Set<number>();
    if (!simulatedParticipants || simulatedParticipants.length === 0) return reserved;
    
    simulatedParticipants.forEach(p => {
      if (p.numbers && Array.isArray(p.numbers)) {
        p.numbers.forEach((n: number) => {
          if (typeof n === 'number' && !isNaN(n) && n <= (raffle?.total_spots || 0)) {
            reserved.add(n);
          }
        });
      }
    });
    return reserved;
  };

  const getPaidNumbers = () => {
    const paid = new Set<number>();
    if (!simulatedParticipants || simulatedParticipants.length === 0) return paid;
    
    simulatedParticipants.slice(0, Math.min(8, simulatedParticipants.length)).forEach(p => {
      if (p.numbers && Array.isArray(p.numbers)) {
        p.numbers.slice(0, 2).forEach((n: number) => {
          if (typeof n === 'number' && !isNaN(n) && n <= (raffle?.total_spots || 0)) {
            paid.add(n);
          }
        });
      }
    });
    return paid;
  };

  const generateGridItems = () => {
    if (!raffle) return [];

    switch (raffle.type) {
      case 'numbers':
        return Array.from({ length: raffle.total_spots }, (_, i) => ({
          id: i + 1,
          label: String(i + 1).padStart(2, '0'),
          type: 'number'
        }));
      
      case 'names':
        const popularNames = [
          'João', 'Maria', 'Pedro', 'Ana', 'Carlos', 'Julia', 'Lucas', 'Beatriz',
          'Gabriel', 'Laura', 'Rafael', 'Isabella', 'Daniel', 'Sophia', 'Bruno',
          'Helena', 'Matheus', 'Alice', 'Thiago', 'Valentina', 'Felipe', 'Luiza',
          'Eduardo', 'Giovanna', 'Leonardo', 'Mariana', 'Gustavo', 'Letícia',
          'Diego', 'Amanda', 'Rodrigo', 'Camila', 'André', 'Larissa', 'Paulo',
          'Fernanda', 'Marcelo', 'Bianca', 'Ricardo', 'Carolina', 'Fernando',
          'Natália', 'Alexandre', 'Gabriela', 'Henrique', 'Vitória', 'Jorge',
          'Yasmin', 'Vinicius', 'Lara'
        ];
        return popularNames.slice(0, raffle.total_spots).map((name, i) => ({
          id: i + 1,
          label: name,
          type: 'name'
        }));
      
      case 'animals':
        const animals = [
          '🦁 Leão', '🐯 Tigre', '🐻 Urso', '🦊 Raposa', '🐺 Lobo', '🐶 Cachorro',
          '🐱 Gato', '🐭 Rato', '🐹 Hamster', '🐰 Coelho', '🦝 Guaxinim', '🐻‍❄️ Urso Polar',
          '🐨 Coala', '🐼 Panda', '🦥 Preguiça', '🦦 Lontra', '🦫 Castor', '🦘 Canguru',
          '🦡 Texugo', '🐾 Patas', '🦔 Ouriço', '🦇 Morcego', '🐴 Cavalo', '🦄 Unicórnio',
          '🦓 Zebra', '🦌 Veado', '🦬 Bisão', '🐮 Vaca', '🐂 Boi', '🐃 Búfalo',
          '🐄 Vaquinha', '🐷 Porco', '🐖 Javali', '🐗 Porco Selvagem', '🐽 Focinho', '🐏 Carneiro',
          '🐑 Ovelha', '🐐 Cabra', '🦙 Lhama', '🦒 Girafa', '🐘 Elefante', '🦣 Mamute',
          '🦏 Rinoceronte', '🦛 Hipopótamo', '🐊 Jacaré', '🐢 Tartaruga', '🦎 Lagarto', '🐍 Cobra',
          '🐲 Dragão', '🦕 Dinossauro'
        ];
        return animals.slice(0, raffle.total_spots).map((animal, i) => ({
          id: i + 1,
          label: animal,
          type: 'animal'
        }));
      
      case 'custom':
        if (raffle.custom_items && raffle.custom_items.length > 0) {
          return raffle.custom_items.slice(0, raffle.total_spots).map((item, i) => ({
            id: i + 1,
            label: item,
            type: 'custom'
          }));
        }
        return Array.from({ length: raffle.total_spots }, (_, i) => ({
          id: i + 1,
          label: `Item ${i + 1}`,
          type: 'custom'
        }));
      
      default:
        return [];
    }
  };

  const gridItems = generateGridItems();

  if (loading) {
    return (
      <BaseLayout variant="centered">
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando prévia da rifa...</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (!raffle) {
    return (
      <BaseLayout variant="centered">
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-4">Rifa não encontrada</h2>
            <Button onClick={() => navigate('/creator-dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar às Minhas Rifas
            </Button>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout variant="wide">
      <div className="space-y-6">
        {/* Header com navegação de volta */}
        <motion.div
          className="flex items-center justify-between py-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/creator-dashboard')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar às Minhas Rifas
            </Button>
            <div className="flex gap-2">
              <Badge variant="secondary" className="gap-1">
                <Eye className="w-4 h-4" />
                Modo Preview
              </Badge>
              <Badge variant="outline" className="gap-1">
                <Clock className="w-4 h-4" />
                Rascunho
              </Badge>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/create-raffle?edit=${id}`)}
            >
              Editar Rifa
            </Button>
            <Button
              onClick={handleActivate}
              disabled={activating}
              className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {activating ? 'Ativando...' : 'Ativar Rifa (10 créditos)'}
            </Button>
          </div>
        </motion.div>

        {/* Aviso de Preview - Dados são exemplos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <ThemedCard className="bg-gradient-to-r from-yellow-500/10 to-orange-600/10 border-yellow-500/20">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    📋 Modo de Pré-visualização
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    Você está visualizando como sua rifa aparecerá para os participantes. 
                    <strong className="text-orange-600"> Todos os participantes e dados mostrados são exemplos fictícios</strong> para demonstração.
                  </p>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
                      <Info className="h-4 w-4" />
                      <span>Os dados reais aparecerão apenas após ativar a rifa e recebermos participações verdadeiras.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        </motion.div>

        {/* Título da Rifa */}
        <motion.div
          className="text-center py-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h1 className="text-4xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
            <Sparkles className="h-8 w-8 text-primary" />
            {raffle.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {raffle.description}
          </p>
        </motion.div>

        {/* Tabs para diferentes visualizações */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="preview" className="gap-2">
                <Eye className="w-4 h-4" />
                Visualização
              </TabsTrigger>
              <TabsTrigger value="stats" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Estatísticas
              </TabsTrigger>
              <TabsTrigger value="details" className="gap-2">
                <AlertCircle className="w-4 h-4" />
                Detalhes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="mt-6 space-y-6">
              {/* Aviso adicional sobre dados de exemplo */}
              <ThemedCard className="bg-gradient-to-r from-blue-500/10 to-indigo-600/10 border-blue-500/20">
                <div className="p-4">
                  <div className="flex items-center gap-2 text-blue-600">
                    <Info className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      👥 Os participantes mostrados abaixo são exemplos fictícios para demonstração
                    </span>
                  </div>
                </div>
              </ThemedCard>

              {/* Grid de Visualização */}
              <ThemedCard>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {raffle.type === 'numbers' && <Hash className="w-5 h-5" />}
                        {raffle.type === 'names' && <Users className="w-5 h-5" />}
                        {raffle.type === 'animals' && <Gift className="w-5 h-5" />}
                        {raffle.type === 'custom' && <Target className="w-5 h-5" />}
                        Escolha sua sorte
                      </CardTitle>
                      <CardDescription>
                        {raffle.total_spots} {raffle.type === 'numbers' ? 'números' : 'opções'} disponíveis
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Por apenas</p>
                      <p className="text-2xl font-bold text-primary">{formatCurrency(raffle.spot_price)}</p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className={`grid gap-2 ${
                    raffle.total_spots <= 50 ? 'grid-cols-10' : 
                    raffle.total_spots <= 100 ? 'grid-cols-10' : 
                    raffle.total_spots <= 500 ? 'grid-cols-10' :
                    'grid-cols-10'
                  }`}>
                    {gridItems.map((item) => {
                      const reservedNumbers = getReservedNumbers();
                      const paidNumbers = getPaidNumbers();
                      const isReserved = reservedNumbers.has(item.id);
                      const isPaid = paidNumbers.has(item.id);
                      
                      return (
                        <div
                          key={item.id}
                          className={`
                            relative p-2 rounded-lg border text-center text-sm font-semibold cursor-pointer transition-all hover:scale-105 min-h-[2.5rem] flex items-center justify-center
                            ${isPaid ? 
                              'bg-gradient-to-br from-green-100 to-green-200 border-green-300 text-green-800 hover:from-green-200 hover:to-green-300' : 
                              isReserved ? 
                              'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-300 text-yellow-800 hover:from-yellow-200 hover:to-yellow-300' :
                              'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 text-blue-900 hover:from-blue-100 hover:to-blue-200 dark:from-slate-100 dark:to-slate-200 dark:text-slate-900'
                            }
                          `}
                          onMouseEnter={() => setHoveredSpot(item.id)}
                          onMouseLeave={() => setHoveredSpot(null)}
                        >
                          <span className="block truncate">{item.label}</span>
                          {hoveredSpot === item.id && (
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap z-10">
                              {isPaid ? '✓ Pago' : isReserved ? '🕐 Reservado' : '✨ Disponível'}
                            </div>
                          )}
                          {isPaid && <CheckCircle2 className="absolute top-0 right-0 w-3 h-3 text-green-600" />}
                          {isReserved && !isPaid && <Clock className="absolute top-0 right-0 w-3 h-3 text-yellow-600" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legenda */}
                  <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded"></div>
                      <span className="text-sm text-muted-foreground">Disponível</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-yellow-100 to-yellow-200 border border-yellow-300 rounded"></div>
                      <span className="text-sm text-muted-foreground">Reservado</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gradient-to-br from-green-100 to-green-200 border border-green-300 rounded"></div>
                      <span className="text-sm text-muted-foreground">Pago</span>
                    </div>
                  </div>
                </CardContent>
              </ThemedCard>

              {/* Card do Prêmio */}
              <ThemedCard className="bg-gradient-to-r from-yellow-500/10 to-orange-600/10 border-yellow-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                    Prêmio da Rifa
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-lg font-medium text-foreground">{raffle.prize_description}</p>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-5 h-5" />
                    <span>Sorteio programado para: <strong>{formatDate(raffle.draw_date)}</strong></span>
                  </div>
                </CardContent>
              </ThemedCard>

              {/* Seção de Participantes (DADOS DE EXEMPLO) */}
              <ThemedCard className="bg-gradient-to-r from-purple-500/10 to-violet-600/10 border-purple-500/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-6 h-6 text-purple-600" />
                      Participantes Ativos
                      <Badge variant="outline" className="text-xs">DEMO</Badge>
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">
                      {simulatedParticipants.length} participantes
                    </span>
                  </div>
                  <CardDescription>
                    Esta seção mostra como os participantes aparecerão quando sua rifa estiver ativa
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {simulatedParticipants.slice(0, 6).map((participant, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="relative">
                          <div className="h-10 w-10 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {participant.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <span className="absolute -top-1 -right-1 text-sm">{participant.country}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{participant.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            {participant.numbers.slice(0, 3).join(', ')}
                            {participant.numbers.length > 3 && ` +${participant.numbers.length - 3}`}
                          </p>
                          {index < 3 && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progresso de Vendas */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-foreground flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Progresso de Vendas
                      </h4>
                      <span className="text-sm font-medium text-primary">
                        {Math.round((getPaidNumbers().size / (raffle?.total_spots || 1)) * 100) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-primary to-purple-600 h-3 rounded-full transition-all duration-500" 
                        style={{ width: `${Math.min(100, Math.round((getPaidNumbers().size / (raffle?.total_spots || 1)) * 100) || 0)}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-green-500/10 rounded-lg">
                        <div className="text-lg font-bold text-green-600">{getPaidNumbers().size || 0}</div>
                        <div className="text-xs text-muted-foreground">Vendidos</div>
                      </div>
                      <div className="p-3 bg-yellow-500/10 rounded-lg">
                        <div className="text-lg font-bold text-yellow-600">{Math.max(0, getReservedNumbers().size - getPaidNumbers().size)}</div>
                        <div className="text-xs text-muted-foreground">Reservados</div>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">{Math.max(0, (raffle?.total_spots || 0) - getReservedNumbers().size)}</div>
                        <div className="text-xs text-muted-foreground">Disponíveis</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </ThemedCard>
            </TabsContent>

            <TabsContent value="stats" className="mt-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <ThemedCard className="bg-gradient-to-r from-green-500/10 to-emerald-600/10 border-green-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      Receita Potencial
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(raffle.total_spots * raffle.spot_price)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Se todos os {raffle.type === 'numbers' ? 'números' : 'itens'} forem vendidos</p>
                  </CardContent>
                </ThemedCard>

                <ThemedCard className="bg-gradient-to-r from-blue-500/10 to-indigo-600/10 border-blue-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      Capacidade Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-foreground">{raffle.total_spots}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total de {raffle.type === 'numbers' ? 'números' : 'opções'} disponíveis</p>
                  </CardContent>
                </ThemedCard>

                <ThemedCard className="bg-gradient-to-r from-purple-500/10 to-violet-600/10 border-purple-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-600" />
                      Preço por Item
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(raffle.spot_price)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Valor de cada {raffle.type === 'numbers' ? 'número' : 'opção'}</p>
                  </CardContent>
                </ThemedCard>

                <ThemedCard className="bg-gradient-to-r from-orange-500/10 to-red-600/10 border-orange-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-orange-600" />
                      Taxa de Ativação
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-foreground">R$ 10,00</p>
                    <p className="text-xs text-muted-foreground mt-1">Valor único para ativar a rifa</p>
                  </CardContent>
                </ThemedCard>
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-6 space-y-6">
              <ThemedCard>
                <CardHeader>
                  <CardTitle>Informações Detalhadas da Rifa</CardTitle>
                  <CardDescription>Detalhes técnicos e configurações da sua rifa</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4">
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-muted-foreground">ID da Rifa:</span>
                      <span className="text-sm font-mono text-foreground">{raffle.id}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-muted-foreground">Tipo:</span>
                      <Badge variant="outline">{raffle.type}</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-muted-foreground">Criado em:</span>
                      <span className="text-sm text-foreground">{formatDate(raffle.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-muted-foreground">Última atualização:</span>
                      <span className="text-sm text-foreground">{formatDate(raffle.updated_at)}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium text-muted-foreground">Organizador:</span>
                      <span className="text-sm text-foreground">{raffle.creator_email}</span>
                    </div>
                  </div>
                </CardContent>
              </ThemedCard>

              <ThemedCard className="bg-gradient-to-r from-yellow-500/10 to-orange-600/10 border-yellow-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    Informações Importantes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Após ativar, a rifa ficará disponível para participações públicas</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Você poderá acompanhar as vendas e participações em tempo real</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">O sorteio será realizado na data configurada</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Compartilhe o link da rifa nas redes sociais para aumentar as vendas</span>
                    </li>
                  </ul>
                </CardContent>
              </ThemedCard>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </BaseLayout>
  );
};

export default RafflePreviewPage;